import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { PerformanceBuilderContext } from 'src/pages/PerformanceBuilder/PerformanceBuilderContext';
import {
  useUpdateBuilderReport,
  QUERY_KEYS as BUILDER_QUERY_KEYS
} from 'src/queries/builder';
import COMMON_CONSTANTS from 'common/commonConstants';
import { Button, toast, appUtils } from 'src/components';
import { ActionModal } from 'src/componentsTailwind/index';
import { useQueryClient } from 'react-query';

const { BUILDER_REPORT_STATUS, BUILDER_COMMON_ERRORS } = COMMON_CONSTANTS;

const SubmitReviewBanner = () => {
  const navigate = useNavigate();
  const {
    data: {
      reportId, isAnyEmpty, editToken, companyMode, companyid
    }
  } = useContext(PerformanceBuilderContext);
  const isCompanySpecific = companyMode === 'company';

  const isLoggedIn = appUtils.isLoggedIn();
  const loggedUser = appUtils.getLoggedUser();

  const [isSubmitReviewModalOpen, setIsSubmitReviewModalOpen] = useState(false);
  const [isSubmittedReviewModalOpen, setIsSubmittedReviewModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const {
    update: updateBuilderReport,
    isLoading: isUpdateBuilderReportLoading
  } = useUpdateBuilderReport(editToken);

  const routeToNewReport = (reload = false) => {
    queryClient.invalidateQueries(BUILDER_QUERY_KEYS.BUILDER_DASHBOARD);
    queryClient.invalidateQueries([BUILDER_QUERY_KEYS.BUILDER, reportId]);
    const currentCompanyId = get(loggedUser, 'companyid') || companyid;

    const url = `/builder/new${currentCompanyId ? `?companyid=${currentCompanyId}` : ''}`;

    if (reload) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  return (
    <>
      <ActionModal
        isOpen={isSubmitReviewModalOpen}
        close={() => setIsSubmitReviewModalOpen(false)}
        title="Are you sure you'd like to submit?"
        content='This indicates that you’re done writing your review.'
        action1Text='Submit Review'
        onAction1={async () => {
          try {
            await updateBuilderReport({
              data: { status: BUILDER_REPORT_STATUS.COMPLETE }
            });

            setIsSubmitReviewModalOpen(false);
            setIsSubmittedReviewModalOpen(true);

            if (!isLoggedIn) {
              window.open(`/builder/${reportId}/view`, '_blank');
            }
          } catch (error) {
            if (error === BUILDER_COMMON_ERRORS.REVIEW_COMPLETED) {
              toast.show(error);
              queryClient.invalidateQueries(
                BUILDER_QUERY_KEYS.BUILDER_DASHBOARD
              );
              queryClient.invalidateQueries([
                BUILDER_QUERY_KEYS.BUILDER,
                reportId
              ]);
              navigate('/builder/dashboard');
              return false;
            }
            toast.error(
              'An error occured when trying to update report. Please try again later.'
            );
          }
        }}
        action2Text='Cancel'
        onAction2={() => setIsSubmitReviewModalOpen(false)}
        isLoading={isUpdateBuilderReportLoading}
      />
      <ActionModal
        isOpen={isSubmittedReviewModalOpen}
        close={() => {}}
        title='Performance Review Submitted'
        content='This review has been submitted. You can view the completed review or create a new one.'
        action1Text='Create New'
        onAction1={() => routeToNewReport(true)}
        action2Text='View Review'
        onAction2={() => {
          queryClient.invalidateQueries(BUILDER_QUERY_KEYS.BUILDER_DASHBOARD);
          queryClient.invalidateQueries([BUILDER_QUERY_KEYS.BUILDER, reportId]);
          setIsSubmittedReviewModalOpen(false);
          navigate(`/builder/${reportId}/view`);
        }}
        type='success'
      />
      <div className='bg-[#f6f6f6] flex items-center -mx-8 p-8 w-[(calc(100%+64px))] justify-between -mt-12'>
        <div className='w-1/2'>
          <p className='m-0 text-gray-500'>
            Done with your evaluation? Make sure to submit the review for this
            review cycle.
          </p>
        </div>
        <div className='w-fit'>
          <Button
            variant='purple'
            classes='px-16 !rounded-md text-sm py-2'
            disabled={
              (isCompanySpecific && isAnyEmpty)
              || isSubmitReviewModalOpen
              || isUpdateBuilderReportLoading
            }
            onClick={() => setIsSubmitReviewModalOpen(true)}
          >
            Submit Review
          </Button>
        </div>
      </div>
    </>
  );
};

export default SubmitReviewBanner;
